import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { isNative } from '@ah/native/runtime';
import { readContacts } from '@ah/native/contacts';
import { SYNC_CONTACTS_M } from '../shared/queries.ts';
import type { SyncContactsResult } from '../shared/types.ts';

const STORAGE_KEY = 'sc_contacts_sync_v1';

type Stored = {
  totalCount: number;
  matchCount: number;
  syncedAt: string;
};

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Stored;
  } catch {
    return null;
  }
}

function writeStored(s: Stored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* swallow */
  }
}

function clearStored(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

export function ContactsToggle() {
  const gql = useGql();
  const [stored, setStored] = useState<Stored | null>(() => readStored());
  const [error, setError] = useState<string | null>(null);

  // Contacts is native-only — but the visibility check has to happen AFTER
  // all hooks have been called, otherwise React sees the hook count change
  // between the first render (show=false → early return) and the second
  // (show=true → useMutation reached) and throws.
  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(isNative());
  }, []);

  const enable = useMutation({
    mutationFn: async () => {
      const contacts = await readContacts();
      if (contacts === null) throw new Error('permission-denied');
      const payload = contacts.map((c) => ({
        deviceContactId: c.id,
        emailHashes: c.emailHashes,
        phoneHashes: c.phoneHashes,
      }));
      const res = await gql.request<{ syncContacts: SyncContactsResult }>(
        SYNC_CONTACTS_M,
        { contacts: payload },
      );
      return res.syncContacts;
    },
    onSuccess: (data) => {
      setError(null);
      const s: Stored = {
        totalCount: data.totalCount,
        matchCount: data.matchCount,
        syncedAt: new Date().toISOString(),
      };
      writeStored(s);
      setStored(s);
    },
    onError: (err) => {
      // Revert the optimistic flip from `toggle()`.
      clearStored();
      setStored(null);
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[ContactsToggle] enable failed:', err);
      if (msg.includes('permission-denied')) {
        setError('Contacts access blocked — enable it in Settings to turn this on.');
      } else {
        setError(`Could not sync contacts: ${msg.slice(0, 160)}`);
      }
    },
  });

  // Turning the toggle off actually deletes the user's stored contacts
  // server-side — we don't just hide the row. This matches the Info.plist
  // claim: "stored… deleted when the user disables the toggle."
  const disable = useMutation({
    mutationFn: async () => {
      await gql.request<{ syncContacts: SyncContactsResult }>(SYNC_CONTACTS_M, {
        contacts: [],
      });
    },
    onSettled: () => {
      clearStored();
      setStored(null);
      setError(null);
    },
  });

  if (!show) return null;

  const isOn = stored !== null;
  const busy = enable.isPending || disable.isPending;

  function toggle() {
    if (busy) return;
    if (isOn) {
      // Flip off immediately, the disable mutation reconciles in the background.
      clearStored();
      setStored(null);
      setError(null);
      disable.mutate();
      return;
    }
    // Optimistic flip on. Reading the address book + uploading 600+ contacts
    // takes a few seconds; without this the toggle reads as "didn't work".
    // Placeholder totals trigger the "Syncing contacts…" subtitle until the
    // mutation lands; onError reverts.
    setStored({ totalCount: 0, matchCount: 0, syncedAt: new Date().toISOString() });
    setError(null);
    enable.mutate();
  }

  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="flex items-center gap-3">
        <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Users size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Contacts</p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle(stored, enable.isPending, disable.isPending)}</p>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isOn}
          aria-label="Toggle contact sync"
          onClick={toggle}
          disabled={busy}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            isOn ? 'bg-primary' : 'bg-muted'
          }`}
        >
          <span
            className={`inline-block size-5 transform rounded-full bg-background shadow transition-transform ${
              isOn ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function subtitle(s: Stored | null, syncing: boolean, deleting: boolean): string {
  if (syncing) return 'Syncing contacts…';
  if (deleting) return 'Removing your contacts…';
  if (!s) return "Turn on so we can find people you already know.";
  if (s.totalCount === 0) return 'No contacts found on this device.';
  if (s.matchCount === 0) {
    return `${s.totalCount} contacts synced. None are on Underlings yet — you'll be able to invite them soon.`;
  }
  const knownNoun = s.matchCount === 1 ? 'person' : 'people';
  return `${s.totalCount} contacts synced — found ${s.matchCount} ${knownNoun} you already know.`;
}
